var BG_B64 = "https://cdn.shopify.com/s/files/1/0772/6012/8453/files/saber_builder_background.png?v=1780455647";
import React, { useState, useEffect, useRef, useMemo } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { createRoot as __cr } from "react-dom/client";
// ── PRODUCTION CDN URLS ─────────────────────────────────────────────────
// Upload your original GLB files to Shopify Files (Settings → Files)
// then paste the CDN URLs below. Each part loads at 100% original quality.
// handle1 + pommel1 are already embedded at full quality.
// Per-part fine-tuning offsets in NORMALIZED units (after auto-scale).
// If a part is misaligned in the assembled saber, nudge it here.
// Find values using GLBTest sliders, or trial and error (small steps like 0.1).
var PART_OFFSETS = {
    core: { x: 0, y: 0, z: 0 },
    emitter: { x: 0, y: 0, z: 0 },
    handle: { x: 0, y: 0, z: 0 },
    crystal: { x: 0, y: 0, z: 0 },
    pommel: { x: 0, y: 0, z: 0 },
};
var NAMES = ["core", "emitter", "handle", "crystal", "pommel"];
// Assembly stack order, top -> bottom. Crystal toggles above/below the handle.
// Reorder these arrays if a part sits in the wrong place.
var STACK_ORDER = {
    bottom: ["emitter", "handle", "crystal", "pommel"],
    top: ["emitter", "crystal", "handle", "pommel"],
};
// Cross-sections at/above this diameter (native Blender units) are treated as the
// solid body / mating surface. Your mating surfaces are ~38-43; stubs are thinner.
var BODY_MIN_DIAM = 47;
var DEV_ALIGN = false; // set true to re-enable the alignment panel + colored connection planes
// Look of the colored (baked) chamber parts. Higher metalness = more metallic
// (color tints the reflections); lower roughness = more polished/shiny.
var COLORED_METALNESS = 0.9;
var COLORED_ROUGHNESS = 0.34;
var SABER2_ENVMAP = 0.4; // env reflection level for parts that honor their authored GLB finish
var RESPECT_GLB_FINISH = true; // true: ALL sabers use the metalness/roughness set in their GLB files
// Saber 2 GLBs were exported a little larger than Saber 1. Scale each Saber 2
// part so it matches Saber 1's size (1.0 = original). Tune these until the
// Saber 2 parts are the same scale as Saber 1 and align cleanly. You can set a
// different value per part if they were exported inconsistently.
var VARIANT_SCALE = {
    // Saber 2 parts are modeled larger; scale the whole saber down to match.
    "em-2": 0.88, "hnd-2": 0.88, "pom-2": 1, "cry-2-open": 0.88, "cry-2-closed": 0.88
};
var TARGET_LEN = 7.2; // world units for the full saber
var CDN_URLS = {
    // shared internal core
    "core-only": "https://cdn.shopify.com/3d/models/e27954f2a97c8124/CoreColored.glb",
    // Saber 1 (colored)
    "em-1": "https://cdn.shopify.com/3d/models/7e6443d0223530c2/emitter1colored-s.glb",
    "hnd-1": "https://cdn.shopify.com/3d/models/a3921bc196d5a74c/handle1colored-s.glb",
    "pom-1": "https://cdn.shopify.com/3d/models/cac67f1f5d324040/pommel1colored-s.glb",
    "cry-1-open": "https://cdn.shopify.com/3d/models/4661892de100550f/crystalchamber1coloredopen-s.glb",
    "cry-1-closed": "https://cdn.shopify.com/3d/models/ac210c6533b58fca/crystalchamber1coloredclosed-s.glb",
    // Saber 2 (colored)
    "em-2": "https://cdn.shopify.com/3d/models/de0b9aabe16a4d94/emitter2colored-s-c.glb",
    "hnd-2": "https://cdn.shopify.com/3d/models/def21057e56b68ce/handle2colored-s-c.glb",
    "pom-2": "https://cdn.shopify.com/3d/models/a2ff497eba783ab5/pommel2colored-s-c.glb",
    "cry-2-open": "https://cdn.shopify.com/3d/models/fb3951a19254eceb/crystalchamber2coloredopen-s-c.glb",
    "cry-2-closed": "https://cdn.shopify.com/3d/models/d31b0c7b5167707f/crystalchamber2coloredclosed-c.glb",
    // Saber 3 (colored)
    "em-3": "https://cdn.shopify.com/3d/models/da5c53eed074de52/emitter3colored.glb",
    "hnd-3": "https://cdn.shopify.com/3d/models/0d5eaa5a725eb808/handle3colored.glb",
    "pom-3": "https://cdn.shopify.com/3d/models/8c405ea7515b497e/pommel3colored.glb",
    "cry-3-open": "https://cdn.shopify.com/3d/models/78ab2370170470ad/crystalchamber3coloredopen.glb",
    "cry-3-closed": "https://cdn.shopify.com/3d/models/f53fd8cf74fd4428/crystalchamber3coloredclosed.glb",
};
// Every loadable part option -> which category container it mounts into
var PART_OPTIONS = [
    { id: "core-only", cat: "core", original: true },
    { id: "em-1", cat: "emitter", original: true }, { id: "em-2", cat: "emitter", original: true }, { id: "em-3", cat: "emitter", original: true },
    { id: "hnd-1", cat: "handle", original: true }, { id: "hnd-2", cat: "handle", original: true }, { id: "hnd-3", cat: "handle", original: true },
    { id: "pom-1", cat: "pommel", original: true }, { id: "pom-2", cat: "pommel", original: true }, { id: "pom-3", cat: "pommel", original: true },
    { id: "cry-1-open", cat: "crystal", original: true }, { id: "cry-1-closed", cat: "crystal", original: true, flip: true },
    { id: "cry-2-open", cat: "crystal", original: true }, { id: "cry-2-closed", cat: "crystal", original: true, flip: true },
    { id: "cry-3-open", cat: "crystal", original: true }, { id: "cry-3-closed", cat: "crystal", original: true, flip: true },
];
function catOf(id) { return id.indexOf("em-") === 0 ? "emitter" : id.indexOf("hnd-") === 0 ? "handle" : id.indexOf("cry-") === 0 ? "crystal" : id.indexOf("pom-") === 0 ? "pommel" : "core"; }
var FINISHES = [
    { id: "silver", name: "Polished Silver", hex: 0xd6dbe2, rough: 0.28 },
    { id: "chrome", name: "Chrome", hex: 0xeef2f6, rough: 0.12 },
    { id: "gunmetal", name: "Gunmetal", hex: 0x6b7079, rough: 0.34 },
    { id: "black", name: "Matte Black", hex: 0x2a2d33, rough: 0.5 },
    { id: "gold", name: "Brushed Gold", hex: 0xd4af6a, rough: 0.34 },
    { id: "copper", name: "Copper", hex: 0xbe7a52, rough: 0.34 },
    { id: "bronze", name: "Antique Bronze", hex: 0x8c6a45, rough: 0.42 },
];
function finishById(id) { for (var i = 0; i < FINISHES.length; i++)
    if (FINISHES[i].id === id)
        return FINISHES[i]; return FINISHES[0]; }
function makeMat(hex) { return new THREE.MeshStandardMaterial({ color: new THREE.Color(hex), metalness: 1.0, roughness: 0.3, envMapIntensity: 1.15, side: THREE.DoubleSide }); }
// Official GLTFLoader + Draco — handles every GLB variant correctly
var _draco = new DRACOLoader();
_draco.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
var _gltf = new GLTFLoader();
_gltf.setDRACOLoader(_draco);
function loadPartGLTF(key, url, hex, preserve, onLoad, onErr) {
    _gltf.load(url, function (gltf) {
        var obj = gltf.scene;
        obj.name = key + "_model";
        obj.traverse(function (o) {
            if (o.isMesh) {
                o.castShadow = false;
                if (preserve) {
                    if (!/Solid_62/.test(o.name)) {
                        var src = Array.isArray(o.material) ? o.material[0] : o.material;
                        var hasVC = !!(o.geometry && o.geometry.attributes && o.geometry.attributes.color);
                        // Saber 2: keep the finish AUTHORED in the GLB (metalness/roughness/maps),
                        // so edits made to the file actually show. Only dial back the environment
                        // reflection that the build's RoomEnvironment adds. Other sabers keep the
                        // build's tuned colored-metal look.
                        var authored = (RESPECT_GLB_FINISH || /-2(-|$)/.test(key)) && src && src.isMeshStandardMaterial;
                        if (authored) {
                            src.envMapIntensity = SABER2_ENVMAP;
                            src.side = THREE.DoubleSide;
                            src.needsUpdate = true;
                            o.material = src;
                        }
                        else {
                            o.material = new THREE.MeshStandardMaterial({
                                color: (src && src.color) ? src.color.clone() : new THREE.Color(0xffffff),
                                map: (src && src.map) ? src.map : null,
                                vertexColors: hasVC,
                                metalness: COLORED_METALNESS, roughness: COLORED_ROUGHNESS, envMapIntensity: 0.9,
                                side: THREE.DoubleSide
                            });
                        }
                        o.userData.preserveColor = true;
                    }
                    // (the Solid_62 crystal mesh is left for the glow override below)
                }
                else {
                    o.material = makeMat(hex);
                }
            }
        });
        onLoad(obj);
    }, undefined, function (e) { if (onErr)
        onErr(e); });
}
var IMG_AETHER = "https://cdn.shopify.com/s/files/1/0772/6012/8453/files/923e29d1-31b6-4bd6-8180-422466be6bbe_b480c6bc-440e-44ff-b962-e4cffb9bedb3.jpg?v=1776439687";
var IMG_HIGHCREST = "https://cdn.shopify.com/s/files/1/0772/6012/8453/files/f12712af-9017-4a34-a466-2b10a4f72b77.jpg?v=1776439730";
var IMG_REGENT = "https://cdn.shopify.com/s/files/1/0772/6012/8453/files/a2a231c7-0eb0-4fe7-a549-f267e8e5de70.jpg?v=1776439751";
var CAT = {
    core: [
        { id: "core-only", name: "Xenopixel Core", price: 90, tag: "BASE", presets: null },
        { id: "build-1", name: "Regent", price: 334.99, tag: "COMPLETE", image: IMG_REGENT, presets: { emitter: "em-1", handle: "hnd-1", crystal: "cry-1", pommel: "pom-1" } },
        { id: "build-2", name: "Aether", price: 334.99, tag: "COMPLETE", image: IMG_AETHER, presets: { emitter: "em-2", handle: "hnd-2", crystal: "cry-2", pommel: "pom-2" } },
        { id: "build-3", name: "Highcrest", price: 334.99, tag: "COMPLETE", image: IMG_HIGHCREST, presets: { emitter: "em-3", handle: "hnd-3", crystal: "cry-3", pommel: "pom-3" } },
    ],
    emitter: [{ id: "em-none", name: "None", price: 0, desc: "No emitter" }, { id: "em-1", name: "Regent Emitter", price: 60, desc: "Regent emitter", image: IMG_REGENT }, { id: "em-2", name: "Aether Emitter", price: 60, desc: "Aether emitter", image: IMG_AETHER }, { id: "em-3", name: "Highcrest Emitter", price: 60, desc: "Highcrest emitter", image: IMG_HIGHCREST }],
    handle: [{ id: "hnd-none", name: "None", price: 0, desc: "No handle" }, { id: "hnd-1", name: "Regent Handle", price: 40, desc: "Regent grip", image: IMG_REGENT }, { id: "hnd-2", name: "Aether Handle", price: 40, desc: "Aether grip", image: IMG_AETHER }, { id: "hnd-3", name: "Highcrest Handle", price: 40, desc: "Highcrest grip", image: IMG_HIGHCREST }],
    crystal: [{ id: "cry-none", name: "None", price: 0, desc: "No chamber" },
        { id: "cry-1", name: "Regent Chamber", price: 60, desc: "Regent crystal chamber", image: IMG_REGENT },
        { id: "cry-2", name: "Aether Chamber", price: 60, desc: "Aether crystal chamber", image: IMG_AETHER },
        { id: "cry-3", name: "Highcrest Chamber", price: 60, desc: "Highcrest crystal chamber", image: IMG_HIGHCREST }],
    pommel: [{ id: "pom-none", name: "None", price: 0, desc: "No pommel" }, { id: "pom-1", name: "Regent Pommel", price: 35, desc: "Regent end cap", image: IMG_REGENT }, { id: "pom-2", name: "Aether Pommel", price: 35, desc: "Aether end cap", image: IMG_AETHER }, { id: "pom-3", name: "Highcrest Pommel", price: 35, desc: "Highcrest end cap", image: IMG_HIGHCREST }],
    blade: [{ id: "blade-none", name: "None", price: 0, desc: "No blade" },
        { id: "blade-standard", name: "Standard Neopixel Blade (36 in)", price: 74.99, desc: "36 inch neopixel blade" },
        { id: "blade-short", name: "Short Neopixel Blade (32 in)", price: 74.99, desc: "32 inch neopixel blade" }],
};
var STEPS = ["core", "emitter", "crystal", "handle", "pommel", "blade"];
var LBL = { core: "Core", emitter: "Emitter", handle: "Handle", crystal: "Crystal", pommel: "Pommel", blade: "Blade" };
var DESC = { core: "Start with core only, or pick a complete pre-built saber.", emitter: "The emitter — where the blade ignites.", handle: "Your grip — the heart of every swing.", crystal: "The crystal chamber — colour your Kyber crystal.", pommel: "The pommel — finish and counterbalance.", blade: "Choose your neopixel blade and length. (Not shown in the 3D preview.)" };
var BASE = 90;
// ============================================================================
// SHOPIFY CHECKOUT: paste the numeric variant ID for each part below.
// Find it in Shopify admin: Products > (product) > the variant URL ends in
// ?variant=NNNNNNNNNN  -> that number. Leave null to skip a part.
// Crystal chambers need BOTH an "-open" and "-closed" id (they're one product
// in the builder via the OPEN/COVERED switch; in Shopify they can be two
// variants of one product, or two products).
// ============================================================================
var VARIANT_IDS = {
    "core-only": 46302500683973, // Base / Core Assembly
    // Emitters
    "em-1": 46302494654661, // Regent Emitter
    "em-2": 46301957456069, // Aether Emitter
    "em-3": 46302495703237, // Highcrest Emitter
    // Handles  (builder keys: -1 Regent, -2 Aether, -3 Highcrest)
    "hnd-1": 46302500126917, // Regent Handle
    "hnd-2": 46302499995845, // Aether Handle
    "hnd-3": 46302500290757, // Highcrest Handle
    // Crystal chambers
    "cry-1": 46302496358597, // Regent Crystal Chamber
    "cry-2": 46302496161989, // Aether Crystal Chamber
    "cry-3": 46302499733701, // Highcrest Crystal Chamber
    // Pommels
    "pom-1": 46302500421829, // Regent Pommel
    "pom-2": 46302500389061, // Aether Pommel
    "pom-3": 46302500454597, // Highcrest Pommel
    // Complete pre-built sabers (one product each)
    "full-regent": 46095021605061, // Regent V3
    "full-aether": 46095021703365, // Aether V3
    "full-highcrest": 46095022588101, // Highcrest V3
    // Neopixel blades
    "blade-standard": 46305634746565, // Standard Neopixel Blade (36 in)
    "blade-short": 46305645002949, // Short Neopixel Blade (32 in)
};
var CART_REDIRECT = "/cart"; // where to send the customer after adding (use "" to stay)
var DFLT = { core: "core-only", emitter: "em-none", handle: "hnd-none", crystal: "cry-none", pommel: "pom-none", blade: "blade-none" };
var AA = "#e07020";
// Camera pivots: world-X = 2.755 - (Z_model_center * 0.01666)
// Review: saber vertical, world center = (2.755, Z_center*0.01666) = (2.755, 2.756)
var FOCAM = {
    core: { pX: 0, pY: 0, zoom: 9.5 },
    emitter: { pX: -2.82, pY: 0, zoom: 5.5 },
    handle: { pX: 0.36, pY: 0, zoom: 5.5 },
    crystal: { pX: 0.16, pY: 0, zoom: 5.2 },
    pommel: { pX: 3.18, pY: 0, zoom: 5.0 },
    review: { pX: 2.755, pY: 2.756, zoom: 11.5 },
};
var PARTCOLS = { core: "#d0d8e0", emitter: "#d8e0e8", handle: "#c8d0da", crystal: "#ccd4de", pommel: "#d0dae4" };
var lerp = function (a, b, t) { return a + (b - a) * t; };
// Complete pre-built sabers sold as ONE Shopify product for a flat price.
var FULL_BUILD_PRICE = 334.99;
var FULL_BUILD_KEY = { "build-1": "full-regent", "build-2": "full-aether", "build-3": "full-highcrest" };
// Returns the core build entry if the current selection IS that prebuilt, untouched.
var matchedFullBuild = function (sel) { return CAT.core.find(function (c) { return c.presets && ["emitter", "handle", "crystal", "pommel"].every(function (k) { return sel[k] === c.presets[k]; }); }) || null; };
var calcPrice = function (sel) { if (matchedFullBuild(sel))
    return FULL_BUILD_PRICE; /* blade is included with complete sabers */ /* blade is included with complete sabers */ return ["emitter", "handle", "crystal", "pommel", "blade"].reduce(function (t, k) { var p = CAT[k].find(function (x) { return x.id === sel[k]; }); return t + (p ? p.price : 0); }, BASE); };
var PGRD = { core: "linear-gradient(145deg,#1a2028,#252e38)", emitter: "linear-gradient(145deg,#1a2535,#253850)", handle: "linear-gradient(145deg,#1a2520,#253a30)", crystal: "linear-gradient(145deg,#1a1a38,#28205a)", pommel: "linear-gradient(145deg,#251a1a,#402820)" };
var PICO = { core: "⚬", emitter: "⬙", handle: "|||", crystal: "◈", pommel: "⦵" };
function setMode(grp, mode) {
    if (!grp)
        return;
    grp.traverse(function (o) {
        if (!o.isMesh || !o.material)
            return;
        // Leave colored (baked) parts and the emissive crystal glow untouched
        if (o.userData && (o.userData.preserveColor || o.userData.isGlow))
            return;
        var m = o.material;
        if (mode === "active") {
            if (m.emissive)
                m.emissive.set(0x906010);
            if ("emissiveIntensity" in m)
                m.emissiveIntensity = 0.45;
            if (m.color)
                m.color.set(1, 1, 1);
        }
        else if (mode === "dim") {
            if (m.emissive)
                m.emissive.set(0);
            if ("emissiveIntensity" in m)
                m.emissiveIntensity = 0;
            if (m.color)
                m.color.set(0.45, 0.47, 0.50);
        }
        else {
            if (m.emissive)
                m.emissive.set(0);
            if ("emissiveIntensity" in m)
                m.emissiveIntensity = 0;
            if (m.color)
                m.color.set(1, 1, 1);
        }
        m.needsUpdate = true;
    });
}
export default function SaberBuilder() {
    var canvasRef = useRef(null), threeRef = useRef({});
    var _0 = useState(0), step = _0[0], setStep = _0[1];
    var _1 = useState(DFLT), sel = _1[0], setSel = _1[1];
    var _2 = useState(195), glowHue = _2[0], setGlowHue = _2[1];
    var _gb = useState(5.0), glowBright = _gb[0], setGlowBright = _gb[1];
    var _fin = useState("silver"), finish = _fin[0], setFinish = _fin[1];
    var _nz = useState({ "em-2": { x: -23.6, y: 0, z: 0, ct: 0, cb: 0, s: 1 }, "cry-1-open": { ct: -1, cb: 1 }, "cry-1-closed": { ct: -7, cb: 0 }, "cry-2-open": { ct: -1, cb: 1 }, "cry-2-closed": { ct: -6.5, cb: 0, s: 1.14 }, "cry-3-open": { ct: -1, cb: 1.5 }, "cry-3-closed": { ct: -7, cb: 0 }, "hnd-1": { ct: -7, cb: 8.5 }, "hnd-2": { ct: -1, cb: 3 }, "hnd-3": { ct: -7, cb: 8.5 }, "pom-1": { ct: 3, cb: 0 }, "pom-2": { ct: 8.5, cb: 0 } }), nudges = _nz[0], setNudges = _nz[1];
    var _3 = useState(function () { return calcPrice(DFLT); }), dispPrice = _3[0], setDispPrice = _3[1];
    var _4 = useState(true), panelOpen = _4[0], setPanelOpen = _4[1];
    var _5 = useState(false), showOrder = _5[0], setShowOrder = _5[1];
    var _6 = useState(false), loaded = _6[0], setLoaded = _6[1];
    var _7 = useState(function () { return typeof window !== "undefined" && window.innerWidth < 768; }), isMobile = _7[0], setIsMobile = _7[1];
    var _cp = useState("top"), crystalPos = _cp[0], setCrystalPos = _cp[1];
    var _cc = useState("open"), crystalCover = _cc[0], setCrystalCover = _cc[1];
    var _da = useState(DEV_ALIGN), devAlign = _da[0], setDevAlign = _da[1];
    // Hidden alignment tool: press F7 to toggle the planes/panel. State only — resets on page refresh.
    useEffect(function () { function k(e) { if (e.key === "F7" || e.keyCode === 118) {
        e.preventDefault();
        setDevAlign(function (v) { return !v; });
    } } window.addEventListener("keydown", k); return function () { window.removeEventListener("keydown", k); }; }, []);
    var price = useMemo(function () { return calcPrice(sel); }, [sel]);
    var isReview = (step === STEPS.length);
    var cType = isReview ? "pommel" : STEPS[step];
    var parts = CAT[cType];
    var cidx = parts.findIndex(function (p) { return p.id === sel[cType]; });
    var canUp = cidx > 0, canDn = cidx < parts.length - 1, picked = parts[cidx];
    function pickCore(id) { var e = CAT.core.find(function (x) { return x.id === id; }); if (e && e.presets)
        setSel(function (p) { return Object.assign({}, p, { core: id }, e.presets); });
    else
        setSel(function (p) { return Object.assign({}, p, { core: id, emitter: "em-none", handle: "hnd-none", crystal: "cry-none", pommel: "pom-none" }); }); }
    function goNext() { if (isReview)
        setShowOrder(true);
    else if (step === STEPS.length - 1)
        setStep(STEPS.length);
    else
        setStep(function (s) { return s + 1; }); }
    function cycleUp() { if (canUp)
        setSel(function (p) { var n = Object.assign({}, p); n[cType] = parts[cidx - 1].id; return n; }); }
    function cycleDn() { if (canDn)
        setSel(function (p) { var n = Object.assign({}, p); n[cType] = parts[cidx + 1].id; return n; }); }
    useEffect(function () { var h = function () { setIsMobile(window.innerWidth < 768); }; window.addEventListener("resize", h); return function () { window.removeEventListener("resize", h); }; }, []);
    useEffect(function () { var s = dispPrice, e = price, t0 = performance.now(); function f(t) { var p = Math.min((t - t0) / 380, 1), q = 1 - Math.pow(1 - p, 3); setDispPrice(Math.round((s + (e - s) * q) * 100) / 100); if (p < 1)
        requestAnimationFrame(f); } requestAnimationFrame(f); }, [price]);
    useEffect(function () {
        var canvas = canvasRef.current, wrap = canvas.parentElement;
        function W() { return wrap.clientWidth; }
        function H() { return wrap.clientHeight; }
        var scene = new THREE.Scene();
        scene.background = null;
        var camera = new THREE.PerspectiveCamera(48, W() / H(), 0.1, 80);
        var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
        renderer.setSize(W(), H());
        renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
        renderer.outputEncoding = THREE.sRGBEncoding;
        renderer.toneMapping = THREE.LinearToneMapping;
        renderer.toneMappingExposure = 1.4;
        // Studio environment so metallic materials reflect & read as real metal
        var _pmrem = new THREE.PMREMGenerator(renderer);
        scene.environment = _pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
        renderer.shadowMap.enabled = false;
        renderer.setClearColor(0x000000, 0);
        // No procedural geometry — using photo background
        // Lights — tuned for silver metallic
        scene.add(new THREE.AmbientLight(0xffffff, 1.4));
        var kL = new THREE.SpotLight(0xfff8f0, 5.0, 20, Math.PI / 7, 0.55, 1.2);
        kL.position.set(0, 5, 1.5);
        kL.castShadow = false;
        scene.add(kL);
        scene.add(kL.target);
        var fL = new THREE.DirectionalLight(0xc0d4ff, 2.2);
        fL.position.set(-5, 3, 3);
        scene.add(fL);
        var rL = new THREE.DirectionalLight(0xeef0f2, 1.0);
        rL.position.set(2, -1, -4);
        scene.add(rL);
        var bL = new THREE.PointLight(0xff9040, 1.0, 8);
        bL.position.set(0, -0.4, 0);
        scene.add(bL);
        var crystalL = new THREE.PointLight(0x00d8ff, 0, 5);
        crystalL.position.set(0.16, 0.05, 0);
        scene.add(crystalL);
        var glowMat = new THREE.MeshBasicMaterial({ color: 0x00d8ff, transparent: true, opacity: 0.0 });
        var glowSph = new THREE.Mesh(new THREE.SphereGeometry(0.16, 20, 20), glowMat);
        glowSph.visible = false;
        scene.add(glowSph);
        threeRef.current = Object.assign(threeRef.current || {}, { glowSph: glowSph, crystalGlowMeshes: [] });
        // Saber group — all parts share same Z coord space
        // rotation.y=PI: emitter on left; position.x=2.755 centres the Z range [-51,+381]
        // Nested groups: sG(rotation) > normGroup(scale+center) > grps[key](parts)
        var spinPivot = new THREE.Group();
        scene.add(spinPivot);
        var sG = new THREE.Group();
        spinPivot.add(sG);
        var normGroup = new THREE.Group();
        sG.add(normGroup);
        // Connection-plane visualizers live in a SEPARATE group (sibling of normGroup)
        // so they never get swept into Box3/vertex measurement of the parts.
        var planeGroup = new THREE.Group();
        sG.add(planeGroup);
        function mkPlane(col) {
            var g = new THREE.CircleGeometry(34, 48);
            var m = new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.32, side: THREE.DoubleSide, depthWrite: false, depthTest: false });
            var mesh = new THREE.Mesh(g, m);
            mesh.rotation.x = Math.PI / 2;
            mesh.visible = false;
            mesh.renderOrder = 999;
            var ring = new THREE.Mesh(new THREE.RingGeometry(33, 35, 48), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.95, side: THREE.DoubleSide, depthWrite: false, depthTest: false }));
            ring.renderOrder = 1000;
            mesh.add(ring);
            return mesh;
        }
        var planeTop = mkPlane(0x40ff7a), planeBot = mkPlane(0xff4060);
        planeGroup.add(planeTop);
        planeGroup.add(planeBot);
        threeRef.current.planeTop = planeTop;
        threeRef.current.planeBot = planeBot;
        threeRef.current.planeGroup = planeGroup;
        threeRef.current.spinPivot = spinPivot;
        var grps = {};
        NAMES.forEach(function (key) {
            var g = new THREE.Group();
            g.name = key;
            g.visible = (key === "core");
            grps[key] = g;
            normGroup.add(g);
        });
        // Camera focus points per part (computed after normalization)
        var focusPoints = {};
        function detectAndStack() {
            // Mount Saber-1 reference variants so long-axis + fixed scale are measured
            // from a known full assembly (keeps scale stable when swapping variants).
            mountVariant("emitter", "em-1");
            mountVariant("handle", "hnd-1");
            mountVariant("crystal", "cry-1");
            mountVariant("pommel", "pom-1");
            var vis0 = {};
            NAMES.forEach(function (k) { vis0[k] = grps[k].visible; grps[k].visible = true; });
            sG.rotation.set(0, 0, 0);
            normGroup.scale.set(1, 1, 1);
            normGroup.position.set(0, 0, 0);
            NAMES.forEach(function (k) { grps[k].position.set(0, 0, 0); });
            sG.updateMatrixWorld(true);
            var box = new THREE.Box3().setFromObject(normGroup);
            var size = box.getSize(new THREE.Vector3());
            var longAxis = (size.y >= size.x && size.y >= size.z) ? "Y" : (size.z >= size.x ? "Z" : "X");
            threeRef.current.longAxis = longAxis;
            threeRef.current.buildRot = longAxis === "Y" ? { axis: "z", ang: Math.PI / 2 }
                : longAxis === "Z" ? { axis: "y", ang: Math.PI / 2 }
                    : { axis: "z", ang: 0 };
            NAMES.forEach(function (k) { grps[k].visible = vis0[k]; });
            threeRef.current.normReady = true;
            restack(); // first call computes & caches fixedScale from full set
            setLoaded(true); // selection effect then mounts the real choice + restacks
        }
        // ── Body-detection stacking ───────────────────────────────
        // Stacks parts on their MAIN BODY (wide section), not full bbox, so thin
        // connector stubs nest into neighbors instead of pushing them apart.
        // ── Body-detection stacking (absolute diameter threshold) ──
        // Mating surfaces are ~38-43mm; thinner sections are connector stubs that
        // nest into neighbors. We measure each part's radial profile along the long
        // axis and treat cross-sections >= BODY_MIN_DIAM as the solid "body".
        var AXI = { x: 0, y: 1, z: 2 };
        function sampleVerts(key) {
            var out = [];
            var v = new THREE.Vector3();
            grps[key].updateWorldMatrix(true, true);
            grps[key].traverse(function (o) {
                if (o.isMesh && o.geometry && o.geometry.attributes && o.geometry.attributes.position) {
                    var pos = o.geometry.attributes.position;
                    var stp = Math.max(1, Math.floor(pos.count / 2500));
                    for (var i = 0; i < pos.count; i += stp) {
                        v.fromBufferAttribute(pos, i);
                        o.localToWorld(v);
                        out.push(v.x, v.y, v.z);
                    }
                }
            });
            return out;
        }
        function radialProfile(key, ax, c1, c2) {
            var V = sampleVerts(key);
            var n = V.length / 3;
            if (!n)
                return null;
            var ai = AXI[ax], i1 = AXI[c1], i2 = AXI[c2];
            var s1 = 0, s2 = 0, minA = Infinity, maxA = -Infinity;
            for (var i = 0; i < n; i++) {
                s1 += V[i * 3 + i1];
                s2 += V[i * 3 + i2];
                var a = V[i * 3 + ai];
                if (a < minA)
                    minA = a;
                if (a > maxA)
                    maxA = a;
            }
            var ctr1 = s1 / n, ctr2 = s2 / n, NB = 64, span = (maxA - minA) || 1;
            var binR = new Float64Array(NB);
            for (var i = 0; i < n; i++) {
                var d1 = V[i * 3 + i1] - ctr1, d2 = V[i * 3 + i2] - ctr2, r = Math.sqrt(d1 * d1 + d2 * d2);
                var bi = Math.min(NB - 1, Math.floor((V[i * 3 + ai] - minA) / span * NB));
                if (r > binR[bi])
                    binR[bi] = r;
            }
            var maxR = 0;
            for (var b = 0; b < NB; b++)
                if (binR[b] > maxR)
                    maxR = binR[b];
            return { V: V, n: n, ai: ai, i1: i1, i2: i2, binR: binR, minA: minA, maxA: maxA, span: span, NB: NB, ctr1: ctr1, ctr2: ctr2, maxR: maxR };
        }
        // Solve 3x3 linear system via Cramer's rule (for circle fit). Returns [x,y] or null.
        function solve3(M, r) {
            var a = M[0][0], b = M[0][1], c = M[0][2], d = M[1][0], e = M[1][1], f = M[1][2], g = M[2][0], h = M[2][1], i = M[2][2];
            var det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
            if (Math.abs(det) < 1e-9)
                return null;
            var id = 1 / det;
            var x = (r[0] * (e * i - f * h) - b * (r[1] * i - f * r[2]) + c * (r[1] * h - e * r[2])) * id;
            var y = (a * (r[1] * i - f * r[2]) - r[0] * (d * i - f * g) + c * (d * r[2] - r[1] * g)) * id;
            return [x, y];
        }
        // Kasa least-squares circle fit; pts = flat [x,y,...]. Returns {cx,cy} or null.
        function kasaFit(pts) {
            var kn = pts.length / 2;
            if (kn < 3)
                return null;
            var Sx = 0, Sy = 0, Sxx = 0, Syy = 0, Sxy = 0, Sxz = 0, Syz = 0, Sz = 0;
            for (var i = 0; i < kn; i++) {
                var x = pts[i * 2], y = pts[i * 2 + 1], z = x * x + y * y;
                Sx += x;
                Sy += y;
                Sxx += x * x;
                Syy += y * y;
                Sxy += x * y;
                Sxz += x * z;
                Syz += y * z;
                Sz += z;
            }
            var s = solve3([[Sxx, Sxy, Sx], [Sxy, Syy, Sy], [Sx, Sy, kn]], [Sxz, Syz, Sz]);
            if (!s)
                return null;
            return { cx: s[0] / 2, cy: s[1] / 2 };
        }
        function median(a) { a = a.slice().sort(function (x, y) { return x - y; }); var n = a.length; return n % 2 ? a[(n - 1) / 2] : (a[n / 2 - 1] + a[n / 2]) / 2; }
        // Find a part's axis WITHOUT guessing a diameter:
        // slice the part along its long axis, circle-fit each slice, keep only the
        // slices that are genuinely circular (low fit error), and take the median
        // center. All coaxial slices share the true axis, so this is very robust.
        function fitCenter(p) {
            var NB = 44, span = p.span, minA = p.minA, ai = p.ai, i1 = p.i1, i2 = p.i2;
            var bins = [];
            for (var b = 0; b < NB; b++)
                bins.push([]);
            for (var i = 0; i < p.n; i++) {
                var bi = Math.min(NB - 1, Math.floor((p.V[i * 3 + ai] - minA) / span * NB));
                bins[bi].push(p.V[i * 3 + i1], p.V[i * 3 + i2]);
            }
            // Fit a circle to every slice; record center + roundness (residual).
            var slices = [];
            for (var b = 0; b < NB; b++) {
                var pc = bins[b], cnt = pc.length / 2;
                if (cnt < 14)
                    continue;
                var f = kasaFit(pc);
                if (!f)
                    continue;
                var sr = 0;
                for (var i = 0; i < cnt; i++) {
                    var dx = pc[i * 2] - f.cx, dy = pc[i * 2 + 1] - f.cy;
                    sr += Math.sqrt(dx * dx + dy * dy);
                }
                var rmean = sr / cnt;
                if (rmean < 1e-6)
                    continue;
                var sr2 = 0;
                for (var i = 0; i < cnt; i++) {
                    var dx = pc[i * 2] - f.cx, dy = pc[i * 2 + 1] - f.cy;
                    var dd = Math.sqrt(dx * dx + dy * dy) - rmean;
                    sr2 += dd * dd;
                }
                slices.push({ cx: f.cx, cy: f.cy, resid: Math.sqrt(sr2 / cnt) / rmean });
            }
            if (slices.length === 0)
                return [p.ctr1, p.ctr2];
            // Use each part's ROUNDEST slices only: sort by roundness, drop clearly
            // non-circular ones, keep the best ~45% (>=5), and take the median center.
            // The median ignores any stragglers, so it's stable for clean parts (em-1)
            // and detailed parts (em-2) alike, with no tolerance to hand-tune.
            slices.sort(function (a, b) { return a.resid - b.resid; });
            var clean = slices.filter(function (s) { return s.resid < 0.45; });
            if (clean.length < 5)
                clean = slices;
            var keep = Math.max(5, Math.floor(clean.length * 0.45));
            var best = clean.slice(0, keep);
            var cs1 = best.map(function (s) { return s.cx; });
            var cs2 = best.map(function (s) { return s.cy; });
            return [median(cs1), median(cs2)];
        }
        function bodyFromProfile(p, absThr) {
            // Long-axis body extent from the >=BODY_MIN_DIAM bins (used for stacking)
            var bmin = Infinity, bmax = -Infinity;
            for (var i = 0; i < p.n; i++) {
                var bi = Math.min(p.NB - 1, Math.floor((p.V[i * 3 + p.ai] - p.minA) / p.span * p.NB));
                if (p.binR[bi] >= absThr) {
                    var lo = p.minA + p.span * bi / p.NB, hi = p.minA + p.span * (bi + 1) / p.NB;
                    if (lo < bmin)
                        bmin = lo;
                    if (hi > bmax)
                        bmax = hi;
                }
            }
            if (bmin === Infinity) {
                bmin = p.minA;
                bmax = p.maxA;
            }
            // Center = rotational axis from clean circular slices (used for concentric alignment)
            var ctr = fitCenter(p);
            return { min: bmin, max: bmax, c1: ctr[0], c2: ctr[1] };
        }
        function restack() {
            if (!threeRef.current.normReady)
                return;
            var crystalPos = threeRef.current.crystalPos || "bottom";
            var review = threeRef.current.isReview;
            var longAxis = threeRef.current.longAxis || "Y";
            var ax = longAxis === "Y" ? "y" : longAxis === "Z" ? "z" : "x";
            var cross = ["x", "y", "z"].filter(function (a) { return a !== ax; });
            var c1 = cross[0], c2 = cross[1];
            // Reset transforms & positions -> measure in native units
            sG.rotation.set(0, 0, 0);
            normGroup.scale.set(1, 1, 1);
            normGroup.position.set(0, 0, 0);
            if (threeRef.current.spinPivot)
                threeRef.current.spinPivot.rotation.set(0, 0, 0);
            NAMES.forEach(function (k) { grps[k].position.set(0, 0, 0); });
            // Per-variant scale = base VARIANT_SCALE * user scale slider
            var _nz = threeRef.current.nudges || {};
            NAMES.forEach(function (k) {
                var mdl = grps[k].children[0];
                if (!mdl)
                    return;
                var id = grps[k].userData.variantId;
                var base = (id && VARIANT_SCALE[id]) || 1;
                var us = (id && _nz[id] && _nz[id].s) || 1;
                mdl.scale.setScalar(base * us);
            });
            sG.updateMatrixWorld(true);
            // Radial profiles + global max radius (unit-independent threshold)
            var prof = {};
            NAMES.forEach(function (k) { prof[k] = grps[k].children.length ? radialProfile(k, ax, c1, c2) : null; });
            var gMax = 0;
            NAMES.forEach(function (k) { if (prof[k] && prof[k].maxR > gMax)
                gMax = prof[k].maxR; });
            // absolute threshold = body radius; if model units look like mm use BODY_MIN_DIAM,
            // otherwise fall back to a proportion of the global max radius.
            var absThr = (gMax >= (BODY_MIN_DIAM / 2)) ? (BODY_MIN_DIAM / 2) : gMax * 0.55;
            var be = {};
            NAMES.forEach(function (k) { be[k] = prof[k] ? bodyFromProfile(prof[k], absThr) : null; });
            // Lay out a set of parts on their body surfaces; returns stack length
            function doLayout(inc) {
                var order = STACK_ORDER[crystalPos];
                var NUD = threeRef.current.nudges || {};
                // Per-part CONNECTION PLANES: top plane = body.max + ct, bottom plane = body.min + cb.
                // Each part is placed so its TOP plane meets the previous part's BOTTOM plane (flush),
                // and the cursor advances by the plane-to-plane span. This propagates down the stack,
                // so a part's connection is defined by its own edges regardless of its neighbours.
                function planeOf(k) { var id = grps[k].userData.variantId; var nd = id && NUD[id]; return { ct: (nd && nd.ct) || 0, cb: (nd && nd.cb) || 0 }; }
                var refKey = (be.handle && inc("handle")) ? "handle" : order.filter(function (k) { return be[k] && inc(k); })[0];
                var refC1 = refKey && be[refKey] ? be[refKey].c1 : 0, refC2 = refKey && be[refKey] ? be[refKey].c2 : 0;
                var cursor = 0;
                order.forEach(function (k) {
                    var b = be[k];
                    if (!b || !inc(k))
                        return;
                    var pl = planeOf(k);
                    var topP = b.max + pl.ct, botP = b.min + pl.cb;
                    var pos = { x: 0, y: 0, z: 0 };
                    pos[c1] = -b.c1;
                    pos[c2] = -b.c2;
                    pos[ax] = cursor - topP; // top connection plane seats at the running cursor
                    grps[k].position.set(pos.x, pos.y, pos.z);
                    cursor -= (topP - botP); // advance to this part's bottom plane = next part's top plane
                });
                var height = -cursor || 1, mid = cursor / 2;
                order.forEach(function (k) { if (be[k] && inc(k))
                    grps[k].position[ax] -= mid; });
                // Core: internal spine -> nest in handle, or center alone if no handle
                if (grps.core.children.length && be.core) {
                    var pos = { x: 0, y: 0, z: 0 };
                    if (be.handle && inc("handle")) {
                        var hCen = grps.handle.position[ax] + (be.handle.min + be.handle.max) / 2;
                        pos[ax] = hCen - (be.core.min + be.core.max) / 2;
                    }
                    else {
                        pos[ax] = -(be.core.min + be.core.max) / 2;
                    }
                    pos[c1] = -be.core.c1;
                    pos[c2] = -be.core.c2;
                    grps.core.position.set(pos.x, pos.y, pos.z);
                }
                // Manual fine-alignment nudges (per selected variant), native units
                var NUD = threeRef.current.nudges || {};
                NAMES.forEach(function (k) {
                    var id = grps[k].userData.variantId;
                    var nd = id && NUD[id];
                    if (nd) {
                        grps[k].position.x += (nd.x || 0);
                        grps[k].position.y += (nd.y || 0);
                        grps[k].position.z += (nd.z || 0);
                    }
                });
                return height;
            }
            // Fixed scale from FULL saber (computed once) so a lone part won't fill screen
            if (!threeRef.current.fixedScale) {
                var fullH = doLayout(function () { return true; });
                threeRef.current.fixedScale = TARGET_LEN / fullH;
            }
            // Lay out only visible parts, but keep the fixed scale
            doLayout(function (k) { return grps[k].visible; });
            normGroup.scale.setScalar(threeRef.current.fixedScale);
            normGroup.position.set(0, 0, 0);
            // Position the adjustable connection-plane visualizers for the ACTIVE part
            (function () {
                var pt = threeRef.current.planeTop, pb = threeRef.current.planeBot, pg = threeRef.current.planeGroup;
                if (!pt || !pb || !pg)
                    return;
                var ak = threeRef.current.activePart;
                var showP = threeRef.current.showPlanes && ak && grps[ak] && grps[ak].visible && be[ak];
                pt.visible = pb.visible = !!showP;
                if (!showP)
                    return;
                var id = grps[ak].userData.variantId;
                var nd = (threeRef.current.nudges || {})[id] || {};
                var b = be[ak];
                var topP = grps[ak].position[ax] + b.max + (nd.ct || 0);
                var botP = grps[ak].position[ax] + b.min + (nd.cb || 0);
                pg.scale.copy(normGroup.scale);
                pg.position.copy(normGroup.position);
                var tp = { x: 0, y: 0, z: 0 }, bp = { x: 0, y: 0, z: 0 };
                tp[ax] = topP;
                bp[ax] = botP;
                tp[c1] = grps[ak].position[c1];
                tp[c2] = grps[ak].position[c2];
                bp[c1] = grps[ak].position[c1];
                bp[c2] = grps[ak].position[c2];
                pt.position.set(tp.x, tp.y, tp.z);
                pb.position.set(bp.x, bp.y, bp.z);
            })();
            applyOrientation(review);
            sG.updateMatrixWorld(true);
            // Camera focus per visible part + whole saber.
            // For each part we frame on its TWO CONNECTION PLANES (the same top/bottom
            // planes used for stacking), not its raw bbox: the pivot is the MIDPOINT of
            // the two planes (the part's true center) and the zoom is sized so both
            // planes sit comfortably inside the frame.
            var fp = {};
            var _NUDc = threeRef.current.nudges || {};
            var _vfov = camera.fov * Math.PI / 180;
            var _aspect = camera.aspect || 1.5;
            var _hfov = 2 * Math.atan(Math.tan(_vfov / 2) * _aspect);
            NAMES.forEach(function (k) {
                if (grps[k].children.length === 0 || !grps[k].visible)
                    return;
                if (k !== "core" && be[k] && prof[k]) {
                    var id = grps[k].userData.variantId, nd = (id && _NUDc[id]) || {};
                    var topA = grps[k].position[ax] + be[k].max + (nd.ct || 0);
                    var botA = grps[k].position[ax] + be[k].min + (nd.cb || 0);
                    var cx = grps[k].position[c1], cz = grps[k].position[c2];
                    function _loc(aVal) { var o = { x: 0, y: 0, z: 0 }; o[ax] = aVal; o[c1] = cx; o[c2] = cz; return normGroup.localToWorld(new THREE.Vector3(o.x, o.y, o.z)); }
                    var wTop = _loc(topA), wBot = _loc(botA);
                    var mid = wTop.clone().add(wBot).multiplyScalar(0.5);
                    var span = wTop.distanceTo(wBot) || 1; // plane-to-plane length
                    var crossR = prof[k].maxR * normGroup.scale.x || span * 0.2; // part radius (world)
                    var distLen = (span * 0.5) / Math.tan(_hfov / 2); // fit length horizontally
                    var distCross = crossR / Math.tan(_vfov / 2); // fit thickness vertically
                    fp[k] = { pX: mid.x, pY: mid.y, zoom: Math.max(3.8, Math.max(distLen, distCross) * 1.8) };
                }
                else {
                    var b = new THREE.Box3().setFromObject(grps[k]);
                    var cc = b.getCenter(new THREE.Vector3()), ss = b.getSize(new THREE.Vector3());
                    fp[k] = { pX: cc.x, pY: cc.y, zoom: Math.max(3.5, Math.max(ss.x, ss.y) * 2.4) };
                }
            });
            var full = new THREE.Box3().setFromObject(normGroup);
            var fc = full.getCenter(new THREE.Vector3());
            var _allDist = (TARGET_LEN * 0.5) / Math.tan(_hfov / 2);
            fp.__all__ = { pX: fc.x, pY: fc.y, zoom: Math.max(TARGET_LEN * 1.75, _allDist * 1.35) };
            threeRef.current.focusPoints = fp;
            // Glow follows the crystal chamber center
            if (grps.crystal.children.length && grps.crystal.visible && threeRef.current.crystalL) {
                var cb = new THREE.Box3().setFromObject(grps.crystal);
                var ccen = cb.getCenter(new THREE.Vector3());
                threeRef.current.crystalL.position.copy(ccen);
                if (threeRef.current.glowSph)
                    threeRef.current.glowSph.position.copy(ccen);
            }
        }
        threeRef.current.restack = restack;
        function applyOrientation(review) {
            // Saber is locked horizontal (emitter left, pommel right) in all modes;
            // it spins around its long axis via the spinPivot.
            var br = threeRef.current.buildRot || { axis: "z", ang: Math.PI / 2 };
            sG.rotation.set(0, 0, 0);
            sG.rotation[br.axis] = br.ang;
        }
        threeRef.current.applyOrientation = applyOrientation;
        // Cache of every loaded part option (keyed by option id)
        var partCache = {};
        // Mount a category's selected variant into its container (swap models in/out)
        function mountVariant(cat, id) {
            if (cat === "core")
                return; // core is the shared internal spine, mounted once
            var g = grps[cat];
            if (g.userData.variantId === id)
                return;
            while (g.children.length)
                g.remove(g.children[0]);
            g.userData.variantId = id;
            var isNone = (!id || id.indexOf("none") >= 0);
            if (!isNone && partCache[id])
                g.add(partCache[id]);
        }
        threeRef.current.mountVariant = mountVariant;
        // Apply a metal finish (color + roughness) to every part, skipping the
        // emissive crystal mesh so the glow is preserved.
        function applyFinish(id) {
            var fin = finishById(id), col = new THREE.Color(fin.hex);
            function paint(root) {
                if (!root)
                    return;
                root.traverse(function (o) {
                    if (o.isMesh && o.material && !/Solid_62/.test(o.name)) {
                        o.material.color.copy(col);
                        o.material.metalness = 1.0;
                        o.material.roughness = fin.rough;
                        o.material.envMapIntensity = 1.15;
                        o.material.needsUpdate = true;
                    }
                });
            }
            Object.keys(partCache).forEach(function (k) { paint(partCache[k]); });
        }
        threeRef.current.applyFinish = applyFinish;
        // Load all part options into the cache
        var loadedN = 0, totalN = PART_OPTIONS.length;
        PART_OPTIONS.forEach(function (opt) {
            loadPartGLTF(opt.id, CDN_URLS[opt.id], PARTCOLS[opt.cat], opt.original, function (obj) {
                var off = PART_OFFSETS[opt.cat] || { x: 0, y: 0, z: 0 };
                obj.position.set(off.x, off.y, off.z);
                var sc = VARIANT_SCALE[opt.id] || 1;
                obj.scale.setScalar(sc);
                if (opt.flip) {
                    obj.rotation.x = Math.PI;
                } // covered chamber is imported upside down
                if (opt.cat === "crystal") {
                    // The actual crystal mesh glows (emissive), for both plain & colored chambers
                    obj.traverse(function (o) {
                        if (o.isMesh && /Solid_62/.test(o.name)) {
                            o.material = new THREE.MeshStandardMaterial({ color: 0x050505, emissive: new THREE.Color(0x00d8ff), emissiveIntensity: 2.8, metalness: 0.0, roughness: 0.25, transparent: true, opacity: 0.95 });
                            o.userData.isGlow = true;
                            if (threeRef.current.crystalGlowMeshes)
                                threeRef.current.crystalGlowMeshes.push(o);
                        }
                    });
                }
                partCache[opt.id] = obj;
                console.log("loaded:", opt.id);
                loadedN++;
                if (loadedN === totalN)
                    finishLoad();
            }, function (e) {
                console.warn("FAILED:", opt.id, e && e.message);
                loadedN++;
                if (loadedN === totalN)
                    finishLoad();
            });
        });
        function finishLoad() {
            // Core is the same model always -> mount once into its container
            if (partCache["core-only"])
                grps.core.add(partCache["core-only"]);
            detectAndStack();
        }
        var cs = { ry: 0, rx: 0.08, pX: 0, tPX: 0, pY: 0, tPY: 0, dist: 9.5, tDist: 9.5, spin: 0 };
        function updateCam() { var cRX = Math.cos(cs.rx), sRX = Math.sin(cs.rx); camera.position.set(cs.pX + Math.sin(cs.ry) * cRX * cs.dist, cs.pY + sRX * cs.dist + 0.4, Math.cos(cs.ry) * cRX * cs.dist); camera.lookAt(cs.pX, cs.pY, 0); }
        threeRef.current = Object.assign(threeRef.current || {}, { renderer: renderer, scene: scene, sG: sG, normGroup: normGroup, grps: grps, crystalL: crystalL, glowMat: glowMat, cs: cs, updateCam: updateCam, focusPoints: {}, normReady: false });
        var drag = false, ox = 0, oy = 0, pinch = null;
        function onDn(e) { drag = true; var s = e.touches ? e.touches[0] : e; ox = s.clientX; oy = s.clientY; }
        function onUp() { drag = false; pinch = null; }
        function onMv(e) {
            if (e.touches && e.touches.length === 2) {
                e.preventDefault();
                var d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
                if (pinch !== null)
                    cs.tDist = Math.max(2, Math.min(20, cs.tDist - (d - pinch) * 0.05));
                pinch = d;
                return;
            }
            if (!drag)
                return;
            var isTouch = !!e.touches;
            if (isTouch)
                e.preventDefault(); // gesture began on the canvas -> drive the saber, don't scroll the page
            var s2 = isTouch ? e.touches[0] : e;
            var mult = isTouch ? 1.75 : 1; // touch is ~75% more sensitive than desktop
            cs.spin += (s2.clientY - oy) * 0.016 * mult; // up/down swipe = rotate
            var tr3 = threeRef.current;
            if (tr3.freeRot && tr3.spinPivot) {
                tr3.spinPivot.rotation.y += (s2.clientX - ox) * 0.012 * mult;
            } // 3-axis mode: horizontal drag tumbles
            else {
                cs.tPX = Math.max(-5, Math.min(5, cs.tPX - (s2.clientX - ox) * 0.006 * mult));
            } // normal: left/right = pan (drag left -> saber moves left)
            ox = s2.clientX;
            oy = s2.clientY;
        }
        function onWh(e) { e.preventDefault(); cs.tDist = Math.max(2, Math.min(20, cs.tDist + e.deltaY * 0.013)); }
        canvas.addEventListener("mousedown", onDn);
        canvas.addEventListener("touchstart", onDn, { passive: true });
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchend", onUp);
        window.addEventListener("mousemove", onMv);
        window.addEventListener("touchmove", onMv, { passive: false });
        canvas.addEventListener("wheel", onWh, { passive: false });
        var raf;
        function tick() { raf = requestAnimationFrame(tick); var tr = threeRef.current; cs.pX = lerp(cs.pX, cs.tPX, 0.055); cs.pY = lerp(cs.pY, cs.tPY, 0.055); cs.dist = lerp(cs.dist, cs.tDist, 0.055); if (tr.spinPivot)
            tr.spinPivot.rotation.x = cs.spin; updateCam(); renderer.render(scene, camera); }
        tick();
        var ro = new ResizeObserver(function () { camera.aspect = W() / H(); camera.updateProjectionMatrix(); renderer.setSize(W(), H()); });
        ro.observe(wrap);
        return function () { cancelAnimationFrame(raf); canvas.removeEventListener("mousedown", onDn); canvas.removeEventListener("touchstart", onDn); window.removeEventListener("mouseup", onUp); window.removeEventListener("touchend", onUp); window.removeEventListener("mousemove", onMv); window.removeEventListener("touchmove", onMv); canvas.removeEventListener("wheel", onWh); ro.disconnect(); renderer.dispose(); };
    }, []);
    useEffect(function () { var tr = threeRef.current; if (!tr.grps)
        return; var g = tr.grps; if (tr.mountVariant) {
        tr.mountVariant("emitter", sel.emitter);
        tr.mountVariant("handle", sel.handle);
        tr.mountVariant("crystal", sel.crystal === "cry-none" ? "cry-none" : (sel.crystal + "-" + crystalCover));
        tr.mountVariant("pommel", sel.pommel);
    } g.core.visible = (sel.emitter === "em-none" && sel.handle === "hnd-none" && sel.crystal === "cry-none" && sel.pommel === "pom-none"); g.emitter.visible = (sel.emitter !== "em-none"); g.handle.visible = (sel.handle !== "hnd-none"); g.crystal.visible = (sel.crystal !== "cry-none"); g.pommel.visible = (sel.pommel !== "pom-none"); var aSlot = isReview ? null : STEPS[step]; Object.keys(g).forEach(function (k) { if (!g[k].visible && k !== "core")
        return; setMode(g[k], !aSlot ? "normal" : k === aSlot ? "active" : "dim"); }); tr.isReview = isReview; tr.crystalPos = crystalPos; var apk = (!isReview && STEPS[step] !== "core") ? STEPS[step] : null; tr.activePart = apk; tr.showPlanes = devAlign && !!(apk && g[apk] && g[apk].visible); if (tr.restack)
        tr.restack(); }, [sel, step, loaded, isReview, crystalPos, crystalCover, devAlign]);
    useEffect(function () { var tr = threeRef.current; if (!tr.crystalL)
        return; var has = (sel.crystal !== "cry-none"); var col = new THREE.Color(); col.setHSL(glowHue / 360, 1.0, 0.5); tr.crystalL.color.copy(col); tr.crystalL.intensity = has ? (glowBright * 0.5) : 0; if (tr.crystalGlowMeshes) {
        tr.crystalGlowMeshes.forEach(function (o) { o.material.emissive.copy(col); o.material.emissiveIntensity = has ? glowBright : 0; o.material.needsUpdate = true; });
    } if (tr.glowMat)
        tr.glowMat.opacity = 0; }, [glowHue, glowBright, sel.crystal, loaded]);
    useEffect(function () {
        var tr = threeRef.current;
        if (!tr.cs || !tr.normReady)
            return;
        var fp = tr.focusPoints || {};
        var key = isReview ? "__all__" : STEPS[step];
        var c = fp[key] || fp.__all__ || { pX: 0, pY: 0, zoom: 9.5 };
        if (tr.applyOrientation)
            tr.applyOrientation(isReview);
        if (isReview) {
            tr.cs.ry = 0;
            tr.cs.rx = 0;
        }
        // snap pivot so camera is centered immediately
        tr.cs.pX = c.pX;
        tr.cs.pY = c.pY;
        tr.cs.tPX = c.pX;
        tr.cs.tPY = c.pY;
        tr.cs.tDist = c.zoom;
    }, [step, loaded, isReview, crystalPos, sel, crystalCover]);
    useEffect(function () {
        var tr = threeRef.current;
        tr.crystalPos = crystalPos;
        tr.isReview = isReview;
        if (tr.restack)
            tr.restack();
    }, [crystalPos, loaded]);
    useEffect(function () {
        var tr = threeRef.current;
        tr.nudges = nudges;
        if (tr.restack)
            tr.restack();
    }, [nudges, loaded]);
    var cartBusyRef = useRef(false); // blocks double-submits (double-click/tap would fire /cart/add.js twice)
    var _ce = useState(null), cartErr = _ce[0], setCartErr = _ce[1]; // in-modal cart error (alert() is blocked in theme-editor previews)
    useEffect(function () { setCartErr(null); }, [sel]); // picking a different part clears the message
    var _sp = useState(false), spin3d = _sp[0], setSpin3d = _sp[1]; // review-screen 3-axis tumble
    useEffect(function () { var tr = threeRef.current; if (!tr.spinPivot)
        return; if (isReview && spin3d) {
        if (!tr.freeRot) {
            tr.__rotY = tr.spinPivot.rotation.y;
            tr.__rotZ = tr.spinPivot.rotation.z;
            tr.freeRot = true;
        }
    }
    else if (tr.freeRot) {
        tr.freeRot = false;
        tr.spinPivot.rotation.y = tr.__rotY;
        tr.spinPivot.rotation.z = tr.__rotZ;
    } }, [spin3d, isReview]);
    useEffect(function () { if (!isReview && spin3d)
        setSpin3d(false); }, [isReview]);
    function addToCart() {
        if (cartBusyRef.current)
            return;
        var items = [];
        function push(id, props) {
            if (!id)
                return;
            var base = String(id).replace(/-(open|closed)$/, ""); // crystal chambers are ONE product whether open or covered
            var v = VARIANT_IDS[base];
            if (!v) {
                console.warn("[saber-builder] no variant ID mapped for part:", id);
                return;
            }
            var it = { id: Number(v), quantity: 1 };
            if (props)
                it.properties = props;
            items.push(it);
        }
        var fb = matchedFullBuild(sel);
        if (fb) {
            // Unchanged pre-built saber -> ONE complete-saber product. It ships WITH a blade,
            // so the chosen length rides along as a line-item property instead of a paid item.
            var bl = CAT.blade.find(function (x) { return x.id === sel.blade; });
            if (!bl || bl.id === "blade-none") {
                setCartErr("Please choose a blade length first — every complete saber includes a neopixel blade.");
                setShowOrder(false);
                setStep(STEPS.indexOf("blade"));
                return;
            }
            push(FULL_BUILD_KEY[fb.id], { "Blade": bl.name });
        }
        else {
            push("core-only");
            if (sel.emitter !== "em-none")
                push(sel.emitter);
            if (sel.crystal !== "cry-none")
                push(sel.crystal);
            if (sel.handle !== "hnd-none")
                push(sel.handle);
            if (sel.pommel !== "pom-none")
                push(sel.pommel);
            if (sel.blade && sel.blade !== "blade-none")
                push(sel.blade); // custom builds: blade is its own paid line item
        }
        if (!items.length) {
            alert("Add your Shopify variant IDs to the VARIANT_IDS map near the top of this file to enable checkout.");
            return;
        }
        cartBusyRef.current = true;
        setCartErr(null);
        fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items: items }) })
            .then(function (res) { if (!res.ok)
            return res.json().then(function (e) { e.httpStatus = res.status; throw e; }); return res.json(); })
            .then(function () { window.location.href = "/cart"; })
            .catch(function (err) {
            cartBusyRef.current = false;
            console.error("Cart error:", err);
            var msg = (err && (err.description || err.message)) || String(err);
            if (/sold out|in your cart|stock|available/i.test(msg)) {
                setCartErr("Out of stock — " + msg + " Please swap that part for another option.");
            }
            else {
                setCartErr("Couldn't add to the Shopify cart" + (err && err.httpStatus ? " (HTTP " + err.httpStatus + ")" : "") + ": " + msg);
            }
        });
    }
    var nextLbl = isReview ? "ADD TO CART" : step === STEPS.length - 1 ? "REVIEW BUILD →" : (LBL[STEPS[step + 1]] || "").toUpperCase() + " →";
    return (React.createElement("div", { style: { display: "flex", flexDirection: "column", height: "calc(100vh - 96px)", background: "#060810", color: "#bcc8d4", fontFamily: "'Share Tech Mono',monospace", overflow: "hidden" } },
        React.createElement("style", { dangerouslySetInnerHTML: { __html: [
                    "@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Share+Tech+Mono&display=swap');",
                    "@keyframes spin{to{transform:rotate(360deg)}}",
                    "*{box-sizing:border-box;margin:0;padding:0}button:focus{outline:none}",
                    "::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:#08090f}::-webkit-scrollbar-thumb{background:rgba(224,112,32,.3);border-radius:2px}",
                    ".snav::-webkit-scrollbar{display:none}",
                    ".hue-sl{-webkit-appearance:none;appearance:none;width:100%;height:14px;border-radius:7px;background:linear-gradient(90deg,hsl(0,100%,50%),hsl(45,100%,50%),hsl(90,100%,50%),hsl(135,100%,50%),hsl(180,100%,50%),hsl(225,100%,50%),hsl(270,100%,50%),hsl(315,100%,50%),hsl(360,100%,50%));outline:none;cursor:pointer;border:1px solid rgba(255,255,255,0.1)}",
                    ".hue-sl::-webkit-slider-thumb{-webkit-appearance:none;width:22px;height:22px;border-radius:50%;background:white;box-shadow:0 0 4px rgba(0,0,0,0.6);cursor:pointer;border:2px solid rgba(0,0,0,0.3)}",
                    ".bs{-webkit-appearance:none;appearance:none;height:3px;border-radius:2px;background:linear-gradient(90deg,#e07020,#4a3020);outline:none}",
                    ".bs::-webkit-slider-thumb{-webkit-appearance:none;width:13px;height:13px;border-radius:50%;background:#e07020;cursor:pointer}"
                ].join("") } }),
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px", borderBottom: "1px solid rgba(224,112,32,0.15)", background: "#07080f", flexShrink: 0 } },
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8 } },
                React.createElement("span", { style: { fontSize: 18, color: AA, textShadow: "0 0 18px " + AA } }, "\u25C6"),
                React.createElement("div", null,
                    React.createElement("div", { style: { fontFamily: "'Orbitron',sans-serif", fontSize: isMobile ? 10 : 13, fontWeight: 900, color: "#e8f0d8", letterSpacing: 2 } }, "IMPERIAL WORKSHOP SABER BUILDER")))),
        cartErr && !showOrder && (React.createElement("div", { onClick: function () { setCartErr(null); }, style: { flexShrink: 0, padding: "8px 12px", background: "rgba(180,40,30,0.18)", borderBottom: "1px solid rgba(220,80,60,0.5)", color: "#ff9a86", fontSize: 10, lineHeight: 1.5, cursor: "pointer" } },
            cartErr,
            "  \u2715")),
        React.createElement("div", { style: { display: "flex", alignItems: "stretch", flexShrink: 0, background: "#07080f", borderBottom: "1px solid rgba(224,112,32,0.08)" } },
            React.createElement("div", { className: "snav", style: { display: "flex", flex: 1, minWidth: 0, overflowX: "auto", padding: "0 8px" } }, [...STEPS, "__rv__"].map(function (s, i) { var isRv = s === "__rv__", sIdx = isRv ? STEPS.length : i, active = step === sIdx, done = step > sIdx, can = true; return (React.createElement("button", { key: s, onClick: function () { setStep(sIdx); }, style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 1, padding: isMobile ? "6px 10px" : "11px 18px", background: "transparent", border: "none", color: active ? AA : done ? "#4a8852" : "#5a6852", cursor: can ? "pointer" : "default", fontFamily: "inherit", minWidth: isMobile ? 48 : 70, flexShrink: 0, borderBottom: (isMobile ? "2px" : "3px") + " solid " + (active ? AA : "transparent"), transition: "all 0.2s" } },
                React.createElement("span", { style: { fontSize: isMobile ? 10 : 14, fontWeight: 700 } }, done ? "✓" : isRv ? "◈" : sIdx + 1),
                React.createElement("span", { style: { fontSize: isMobile ? 7 : 10, letterSpacing: 1 } }, isRv ? "REVIEW" : LBL[s] ? (isMobile ? LBL[s].slice(0, 3) : LBL[s]).toUpperCase() : ""))); })),
            React.createElement("div", { style: { display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end", padding: "0 16px", flexShrink: 0 } },
                React.createElement("div", { style: { fontSize: 6, color: "#4a5840", letterSpacing: 3, marginBottom: 1 } }, "BUILD TOTAL"),
                React.createElement("div", { style: { fontFamily: "'Orbitron',sans-serif", fontSize: isMobile ? 15 : 20, fontWeight: 700, color: AA, textShadow: "0 0 12px " + AA } },
                    "$",
                    dispPrice))),
        React.createElement("div", { style: { display: "flex", flex: 1, overflow: "hidden", flexDirection: isMobile ? "column" : "row" } },
            React.createElement("div", { style: { position: "relative", overflow: "hidden", background: "transparent", flex: isMobile ? "0 0 42vh" : "0 0 " + (panelOpen ? "58%" : "100%"), height: isMobile ? undefined : "100%", minHeight: isMobile ? 220 : undefined, transition: "flex 0.3s ease" } },
                React.createElement("div", { ref: function (el) { if (el)
                        el.style.setProperty("display", "block", "important"); }, style: { position: "absolute", inset: 0, backgroundImage: "url(" + BG_B64 + ")", backgroundSize: "cover", backgroundPosition: "center 60%", zIndex: 0, display: "block" } }),
                React.createElement("div", { style: { position: "absolute", inset: 0, background: "rgba(4,6,12,0.10)", zIndex: 1, display: "block" } }),
                React.createElement("div", { style: { position: "absolute", inset: 0, pointerEvents: "none", zIndex: 4, background: "radial-gradient(ellipse at 50% 40%,transparent 25%,rgba(2,4,10,0.45) 100%)" } }),
                React.createElement("canvas", { ref: canvasRef, style: { position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", cursor: "grab", zIndex: 3 } }),
                !isReview && loaded && (React.createElement("div", { style: { position: "absolute", top: 10, left: 0, right: 0, zIndex: 6, pointerEvents: "none" } }, STEPS.slice(1).map(function (part) { var xp = { emitter: 18, handle: 52, crystal: 38, pommel: 82 }[part] || 50; var act = STEPS[step] === part, zoom = (step > 0 && step < STEPS.length); if (zoom && !act)
                    return null; var vis = { emitter: sel.emitter !== "em-none", handle: sel.handle !== "hnd-none", crystal: sel.crystal !== "cry-none", pommel: sel.pommel !== "pom-none" }[part]; if (!vis && !act)
                    return null; return (React.createElement("div", { key: part, style: { position: "absolute", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, transform: "translateX(-50%)", left: zoom ? "50%" : xp + "%", opacity: act ? 1 : 0.45, transition: "all 0.5s" } },
                    React.createElement("div", { style: { width: 5, height: 5, borderRadius: "50%", background: act ? AA : "#6a8060", boxShadow: act ? "0 0 8px " + AA : undefined } }),
                    React.createElement("span", { style: { fontSize: 7, color: "#c8d8c0", letterSpacing: 2, background: "rgba(6,8,16,0.78)", padding: "2px 7px", borderRadius: 6 } }, part.toUpperCase()))); }))),
                isReview && loaded && (React.createElement("div", { style: { position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)", zIndex: 6, pointerEvents: "none", display: "flex", alignItems: "center", gap: 6, background: "rgba(6,8,16,0.75)", border: "1px solid rgba(224,112,32,0.27)", borderRadius: 20, padding: "5px 14px", backdropFilter: "blur(6px)", fontFamily: "'Orbitron',sans-serif", fontSize: 9, color: "#e8f0d8", letterSpacing: 2 } }, "\u25C6 YOUR COMPLETE SABER")),
                loaded && !isReview && step > 0 && (React.createElement(React.Fragment, null,
                    React.createElement("button", { onClick: cycleUp, disabled: !canUp, style: { position: "absolute", top: "17%", left: "50%", transform: "translateX(-50%)", zIndex: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "rgba(6,8,16,0.78)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: isMobile ? "6px 16px" : "10px 24px", cursor: "pointer", backdropFilter: "blur(8px)", opacity: canUp ? 0.92 : 0.45, color: "#e8f0d8" } },
                        React.createElement("span", { style: { fontSize: isMobile ? 16 : 24, color: AA, lineHeight: 1 } }, "\u25B2"),
                        canUp && React.createElement("span", { style: { fontSize: isMobile ? 7 : 10, color: "#c8d8c0", letterSpacing: 1, textAlign: "center", maxWidth: isMobile ? 110 : 170 } }, parts[cidx - 1].name)),
                    React.createElement("button", { onClick: cycleDn, disabled: !canDn, style: { position: "absolute", bottom: "17%", left: "50%", transform: "translateX(-50%)", zIndex: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "rgba(6,8,16,0.78)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 12, padding: isMobile ? "6px 16px" : "10px 24px", cursor: "pointer", backdropFilter: "blur(8px)", opacity: canDn ? 0.92 : 0.45, color: "#e8f0d8" } },
                        canDn && React.createElement("span", { style: { fontSize: isMobile ? 7 : 10, color: "#c8d8c0", letterSpacing: 1, textAlign: "center", maxWidth: isMobile ? 110 : 170 } }, parts[cidx + 1].name),
                        React.createElement("span", { style: { fontSize: isMobile ? 16 : 24, color: AA, lineHeight: 1 } }, "\u25BC")),
                    React.createElement("div", { style: { position: "absolute", left: 12, bottom: 12, zIndex: 8, background: "rgba(6,8,16,0.8)", border: "1px solid rgba(224,112,32,0.3)", borderRadius: 20, padding: isMobile ? "5px 14px" : "8px 18px", fontSize: isMobile ? 10.5 : 14, color: AA, letterSpacing: 2, fontWeight: 700 } },
                        cidx + 1,
                        " / ",
                        parts.length))),
                !loaded && (React.createElement("div", { style: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 10, background: "rgba(6,8,16,0.96)" } },
                    React.createElement("div", { style: { width: 28, height: 28, border: "2px solid #1e2a20", borderTop: "2px solid " + AA, borderRadius: "50%", animation: "spin 0.85s linear infinite", marginBottom: 10 } }),
                    React.createElement("div", { style: { fontSize: 8, color: "#4a6040", letterSpacing: 4 } }, "FORGE LOADING\u2026"))),
                devAlign && loaded && !isReview && ["emitter", "handle", "crystal", "pommel"].indexOf(STEPS[step]) >= 0 && (sel[STEPS[step]] || "").indexOf("none") < 0 && (function () {
                    var oid = STEPS[step] === "crystal" ? (sel.crystal + "-" + crystalCover) : sel[STEPS[step]];
                    var nd = nudges[oid] || { x: 0, y: 0, z: 0, ct: 0, cb: 0, s: 1 };
                    var setAx = function (axk, v) { setNudges(function (o) { var n = Object.assign({}, o); var cur = Object.assign({ x: 0, y: 0, z: 0, ct: 0, cb: 0, s: 1 }, n[oid]); cur[axk] = v; n[oid] = cur; return n; }); };
                    var mk = function (axk, lbl, mn, mx, st) {
                        return (React.createElement("div", { key: axk, style: { display: "flex", alignItems: "center", gap: 5, marginBottom: 3 } },
                            React.createElement("span", { style: { width: 48, fontSize: 8, color: "#8a9870" } }, lbl),
                            React.createElement("input", { type: "range", min: mn, max: mx, step: st, value: axk === "s" ? (nd.s !== undefined ? nd.s : 1) : (nd[axk] || 0), onChange: function (e) { setAx(axk, +e.target.value); }, style: { flex: 1, minWidth: 0, accentColor: "#e07020", height: 3 } }),
                            React.createElement("span", { style: { width: 34, fontSize: 8, color: "#e07020", textAlign: "right", fontFamily: "monospace" } }, (axk === "s" ? (nd.s !== undefined ? nd.s : 1) : (nd[axk] || 0)).toFixed(axk === "s" ? 2 : 1))));
                    };
                    var readout = oid + "  x:" + (nd.x || 0).toFixed(1) + "  y:" + (nd.y || 0).toFixed(1) + "  z:" + (nd.z || 0).toFixed(1) + "  ct:" + (nd.ct || 0).toFixed(1) + "  cb:" + (nd.cb || 0).toFixed(1) + "  s:" + (nd.s !== undefined ? nd.s : 1).toFixed(2);
                    return (React.createElement("div", { style: { position: "absolute", top: 10, left: 10, zIndex: 9, background: "rgba(6,8,16,0.92)", border: "1px solid rgba(224,112,32,0.35)", borderRadius: 8, padding: "10px 12px", width: 210, backdropFilter: "blur(6px)" } },
                        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 } },
                            React.createElement("span", { style: { fontSize: 8, color: "#e07020", letterSpacing: 1, fontFamily: "monospace" } },
                                "ALIGN: ",
                                oid),
                            React.createElement("button", { onClick: function () { setNudges(function (o) { var n = Object.assign({}, o); n[oid] = { x: 0, y: 0, z: 0, ct: 0, cb: 0, s: 1 }; return n; }); }, style: { fontSize: 7, color: "#9ab", background: "#161e28", border: "1px solid #2a3645", borderRadius: 4, padding: "2px 5px", cursor: "pointer" } }, "RESET")),
                        mk("x", "X side", -100, 100, 0.5),
                        mk("z", "Z side", -100, 100, 0.5),
                        mk("y", "Y length", -100, 100, 0.5),
                        React.createElement("div", { style: { borderTop: "1px solid #1c2530", marginTop: 5, paddingTop: 5, fontSize: 7, color: "#40ff7a", letterSpacing: 1, marginBottom: 2 } }, "CONNECTION PLANES"),
                        mk("ct", "Top \u25B2 (green)", -120, 120, 0.5),
                        mk("cb", "Bottom \u25BC (red)", -120, 120, 0.5),
                        React.createElement("div", { style: { borderTop: "1px solid #1c2530", marginTop: 5, paddingTop: 5 } }, mk("s", "Scale", 0.3, 2.0, 0.01)),
                        React.createElement("div", { style: { fontSize: 7, color: "#7a8a9a", marginTop: 6, fontFamily: "monospace", wordBreak: "break-all", background: "#070a0f", padding: "4px 5px", borderRadius: 4, lineHeight: 1.5 } }, readout),
                        React.createElement("div", { style: { fontSize: 7, color: "#445", marginTop: 4, lineHeight: 1.4 } }, "line it up, then copy the line above to me")));
                })(),
                loaded && !isReview && STEPS[step] === "crystal" && sel.crystal !== "cry-none" && (React.createElement("div", { style: { position: "absolute", top: isMobile ? 6 : 48, right: isMobile ? 6 : 10, zIndex: 10, background: "rgba(6,8,16,0.92)", border: "1px solid rgba(224,112,32,0.35)", borderRadius: 8, padding: isMobile ? "6px 8px" : "9px 11px", width: isMobile ? 132 : 150, backdropFilter: "blur(6px)" } },
                    React.createElement("div", { style: { fontSize: 8, color: "#e07020", letterSpacing: 1, marginBottom: isMobile ? 5 : 7, fontFamily: "monospace" } }, "CHAMBER STYLE"),
                    React.createElement("div", { style: { display: "flex", gap: 6 } }, [["open", "OPEN"], ["closed", "COVERED"]].map(function (o) {
                        var on = crystalCover === o[0];
                        return (React.createElement("button", { key: o[0], onClick: function () { setCrystalCover(o[0]); }, style: { flex: 1, padding: "7px 4px", background: on ? "linear-gradient(135deg,#7a2800,#e07020)" : "#11161e", border: "1px solid " + (on ? "#e07020" : "#263040"), color: on ? "#ffe8c8" : "#8a9870", borderRadius: 5, cursor: "pointer", fontSize: 9, fontFamily: "monospace", fontWeight: on ? "bold" : "normal" } }, o[1]));
                    })))),
                loaded && isReview && (React.createElement("label", { style: { position: "absolute", top: 10, right: 10, zIndex: 9, display: "flex", alignItems: "center", gap: 7, background: "rgba(6,8,16,0.85)", border: "1px solid rgba(224,112,32,0.3)", borderRadius: 8, padding: "8px 12px", cursor: "pointer", color: "#c8d8c0", fontSize: 9, letterSpacing: 1, backdropFilter: "blur(6px)" } },
                    React.createElement("input", { type: "checkbox", checked: spin3d, onChange: function (e) { setSpin3d(e.target.checked); }, style: { accentColor: "#e07020", cursor: "pointer" } }),
                    React.createElement("span", null,
                        "3-AXIS ROTATE",
                        React.createElement("br", null),
                        React.createElement("span", { style: { fontSize: 7, color: "#8a9870", letterSpacing: 0.5 } }, "drag to tumble freely")))),
                loaded && (React.createElement("button", { onClick: goNext, style: { position: "absolute", bottom: 52, right: 14, zIndex: 9, padding: isMobile ? "10px 18px" : "15px 28px", background: "linear-gradient(135deg,#7a2800," + AA + ")", border: "1px solid " + AA, color: "#ffe8c8", borderRadius: 8, cursor: "pointer", fontSize: isMobile ? 9 : 13, fontFamily: "'Share Tech Mono',monospace", letterSpacing: isMobile ? 1 : 2, fontWeight: 700, boxShadow: "0 4px 16px rgba(224,112,32,0.4)" } }, nextLbl)),
                loaded && !panelOpen && (React.createElement("button", { onClick: function () { setPanelOpen(true); }, style: { position: "absolute", zIndex: 11, top: isMobile ? undefined : "50%", bottom: isMobile ? 64 : undefined, right: isMobile ? undefined : 0, left: isMobile ? "50%" : undefined, transform: isMobile ? "translateX(-50%)" : "translateY(-50%)", padding: isMobile ? "9px 18px" : "16px 9px", background: "linear-gradient(135deg,#7a2800," + AA + ")", border: "1px solid " + AA, color: "#ffe8c8", borderRadius: isMobile ? 20 : "8px 0 0 8px", cursor: "pointer", fontSize: 9, fontFamily: "'Share Tech Mono',monospace", letterSpacing: 2, fontWeight: 700, boxShadow: "0 4px 16px rgba(224,112,32,0.45)", writingMode: isMobile ? undefined : "vertical-rl" } }, isMobile ? ("▲ " + LBL[cType].toUpperCase()) : "PARTS ◄")),
                React.createElement("div", { style: { position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 6, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", background: "linear-gradient(0deg,rgba(6,8,16,0.92),transparent)" } },
                    devAlign ? (React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
                        React.createElement("span", { style: { fontSize: isMobile ? 10 : 16, color: "#93a888" } }, "\u263E"),
                        React.createElement("input", { type: "range", className: "bs", min: "40", max: "220", defaultValue: "140", onChange: function (e) { var tr = threeRef.current; if (tr.renderer)
                                tr.renderer.toneMappingExposure = +e.target.value / 100; }, style: { width: isMobile ? 80 : 140, cursor: "pointer", background: "transparent" } }),
                        React.createElement("span", { style: { fontSize: isMobile ? 10 : 16, color: "#93a888" } }, "\u2600"))) : (React.createElement("span", null)),
                    !isMobile && React.createElement("span", { style: { fontSize: 7, color: "#aabfa8", letterSpacing: 1, background: "rgba(6,8,16,0.78)", padding: "3px 8px", borderRadius: 6 } }, "SCROLL TO ZOOM \u00B7 DRAG TO ROTATE"))),
            React.createElement("div", { style: { display: "flex", flexDirection: "column", flexShrink: 0, overflow: "hidden", borderLeft: isMobile ? "none" : "1px solid rgba(224,112,32,0.08)", borderTop: isMobile ? "1px solid rgba(224,112,32,0.08)" : "none", background: "linear-gradient(180deg,#0a0b14,#060810)", width: isMobile ? "100%" : (panelOpen ? "42%" : "0px"), height: isMobile ? (panelOpen ? "54%" : "0px") : "auto", transition: "width 0.35s ease,height 0.35s ease" } },
                React.createElement("div", { onClick: function () { setPanelOpen(function (o) { return !o; }); }, style: { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "8px 16px", cursor: "pointer", background: "rgba(6,8,16,0.8)", borderBottom: "1px solid rgba(224,112,32,0.08)", flexShrink: 0, userSelect: "none" } },
                    React.createElement("div", { style: { flex: 1, height: 1, background: "rgba(224,112,32,0.18)" } }),
                    React.createElement("span", { style: { fontSize: 8, color: AA, letterSpacing: 2, whiteSpace: "nowrap" } }, panelOpen ? (isMobile ? "▼ HIDE" : "◄ HIDE") : (isMobile ? "▲ " + LBL[cType].toUpperCase() : "PARTS ►")),
                    React.createElement("div", { style: { flex: 1, height: 1, background: "rgba(224,112,32,0.18)" } })),
                React.createElement("div", { style: { flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 } }, isReview ? React.createElement(ReviewSummary, { sel: sel, price: price }) : React.createElement(PartChooser, { step: step, cType: cType, parts: parts, sel: sel, setSel: setSel, pickCore: pickCore, picked: picked, glowHue: glowHue, setGlowHue: setGlowHue, glowBright: glowBright, setGlowBright: setGlowBright })))),
        showOrder && React.createElement(OrderModal, { sel: sel, price: price, cartErr: cartErr, onClose: function () { setShowOrder(false); setCartErr(null); }, onCheckout: addToCart })));
}
function PartChooser(props) {
    var step = props.step, cType = props.cType, parts = props.parts, sel = props.sel, setSel = props.setSel, pickCore = props.pickCore, picked = props.picked, glowHue = props.glowHue, setGlowHue = props.setGlowHue, glowBright = props.glowBright, setGlowBright = props.setGlowBright;
    var hasCrystal = (sel.crystal !== "cry-none");
    return (React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 10, flex: 1 } },
        React.createElement("div", { style: { paddingBottom: 8, borderBottom: "1px solid rgba(224,112,32,0.08)" } },
            React.createElement("div", { style: { fontFamily: "'Orbitron',sans-serif", fontSize: 11, fontWeight: 700, color: "#e8f0d8", letterSpacing: 2, marginBottom: 3, display: "flex", alignItems: "baseline", gap: 7 } },
                React.createElement("span", { style: { fontSize: 7, color: "#4a5840" } },
                    "0",
                    step + 1),
                LBL[cType].toUpperCase()),
            React.createElement("div", { style: { fontSize: 8, color: "#364030", lineHeight: 1.5 } }, DESC[cType])),
        React.createElement("div", { style: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 6 } }, parts.map(function (part) { return (React.createElement(PartCard, { key: part.id, part: cType === "blade" && part.price > 0 && matchedFullBuild(sel) ? Object.assign({}, part, { price: 0 }) : part, type: cType, active: sel[cType] === part.id, onPick: function () { if (cType === "core")
                pickCore(part.id);
            else
                setSel(function (p) { var n = Object.assign({}, p); n[cType] = part.id; return n; }); } })); })),
        hasCrystal && (React.createElement("div", { style: { background: "rgba(6,8,18,0.85)", border: "1px solid rgba(224,112,32,0.15)", borderRadius: 8, padding: "12px" } },
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 } },
                React.createElement("div", { style: { width: 18, height: 18, borderRadius: "50%", background: "hsl(" + glowHue + ",100%,50%)", boxShadow: "0 0 10px hsl(" + glowHue + ",100%,50%)", flexShrink: 0 } }),
                React.createElement("span", { style: { fontFamily: "'Orbitron',sans-serif", fontSize: 9, color: "#c0d0d8", letterSpacing: 2 } }, "KYBER GLOW COLOR")),
            React.createElement("input", { type: "range", className: "hue-sl", min: "0", max: "359", value: glowHue, onChange: function (e) { setGlowHue(+e.target.value); } }),
            React.createElement("div", { style: { display: "flex", gap: 5, marginTop: 10, justifyContent: "center", flexWrap: "wrap" } }, [[0, "Sith"], [120, "Forest"], [180, "Jedi"], [200, "Cal"], [240, "Guardian"], [280, "Mace"], [300, "Vader"], [340, "Ahsoka"]].map(function (item) { var h = item[0], n = item[1]; return (React.createElement("div", { key: h, onClick: function () { setGlowHue(h); }, style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, cursor: "pointer" } },
                React.createElement("div", { style: { width: 14, height: 14, borderRadius: "50%", background: "hsl(" + h + ",100%,50%)", boxShadow: "0 0 6px hsl(" + h + ",100%,50%)", border: Math.abs(glowHue - h) < 15 ? "2px solid white" : "2px solid transparent", transition: "all 0.15s" } }))); })),
            React.createElement("div", { style: { display: "flex", justifyContent: "space-between", marginTop: 12, marginBottom: 3 } },
                React.createElement("span", { style: { fontSize: 8, color: "#8a9870", letterSpacing: 1 } }, "DIM"),
                React.createElement("span", { style: { fontSize: 8, color: "#8a9870", letterSpacing: 1 } }, "BRIGHT")),
            React.createElement("input", { type: "range", min: "0", max: "10", step: "0.2", value: glowBright, onChange: function (e) { setGlowBright(+e.target.value); }, style: { display: "block", width: "100%", accentColor: "#e07020", height: 3, cursor: "pointer" } }))),
        picked && (React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "7px 0", borderTop: "1px solid rgba(224,112,32,0.08)", fontSize: 8, marginTop: "auto" } },
            React.createElement("span", { style: { color: "#5a7055" } }, picked.name),
            React.createElement("span", { style: { fontFamily: "'Orbitron',sans-serif", fontWeight: 700, color: AA, fontSize: 9 } }, picked.price > 0 ? "+$" + picked.price : "Included")))));
}
function ReviewSummary(props) { var sel = props.sel, price = props.price; return (React.createElement("div", { style: { display: "flex", flexDirection: "column", flex: 1 } },
    React.createElement("div", { style: { fontFamily: "'Orbitron',sans-serif", fontSize: 10, fontWeight: 700, color: "#e8f0d8", letterSpacing: 3, marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid rgba(224,112,32,0.15)" } }, "BUILD SUMMARY"),
    STEPS.map(function (k) { var fb = matchedFullBuild(sel); if (fb && k !== "core" && k !== "blade")
        return null; var p = CAT[k].find(function (x) { return x.id === sel[k]; }); if (!p)
        return null; var lbl = (k === "core" && fb) ? "Saber" : LBL[k]; var nm = p.name, pr = p.price > 0 ? "+$" + p.price : "—"; if (k === "core") {
        if (fb) {
            nm = fb.name + " — Complete Saber (blade included)";
            pr = "$" + FULL_BUILD_PRICE;
        }
        else {
            nm = "Xenopixel Core";
            pr = "+$" + BASE;
        }
    } if (k === "blade" && fb) {
        pr = p.price > 0 ? "Included" : "—";
    } return (React.createElement("div", { key: k, style: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #0e1520", fontSize: 9 } },
        React.createElement("span", { style: { color: "#364038", textTransform: "uppercase", width: 55, flexShrink: 0 } }, lbl),
        React.createElement("span", { style: { flex: 1, color: "#9ab0a8" } }, nm),
        React.createElement("span", { style: { fontFamily: "'Orbitron',sans-serif", fontWeight: 700, color: AA, fontSize: 9 } }, pr))); }),
    React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(224,112,32,0.15)", fontFamily: "'Orbitron',sans-serif", fontSize: 10, color: "#e8f0d8", letterSpacing: 2 } },
        React.createElement("span", null, "TOTAL"),
        React.createElement("span", { style: { fontSize: 20, fontWeight: 700, color: AA } },
            "$",
            price)))); }
function PartCard(props) { var part = props.part, type = props.type, active = props.active, onPick = props.onPick; var _e = useState(false), err = _e[0], setErr = _e[1]; var hasImg = part.image && !err; return (React.createElement("button", { onClick: onPick, style: { position: "relative", padding: 0, border: "1px solid " + (active ? AA : "#181e28"), background: active ? "#110d07" : "#0a0c14", borderRadius: 7, cursor: "pointer", textAlign: "left", color: "inherit", fontFamily: "inherit", overflow: "hidden", transition: "all 0.16s", boxShadow: active ? "0 0 14px " + AA + "30" : undefined } },
    React.createElement("div", { style: { position: "relative", width: "100%", paddingTop: "60%", background: "#090c14", overflow: "hidden" } },
        hasImg ? React.createElement("img", { src: part.image, alt: part.name, onError: function () { setErr(true); }, style: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", padding: 6 } }) : React.createElement("div", { style: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: PGRD[type] || PGRD.core } },
            React.createElement("span", { style: { fontSize: 22, opacity: 0.38 } }, PICO[type] || "◎")),
        active && React.createElement("div", { style: { position: "absolute", top: 5, right: 5, width: 16, height: 16, borderRadius: "50%", background: AA, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#000", fontWeight: 700 } }, "\u25C6"),
        type === "core" && part.presets && React.createElement("div", { style: { position: "absolute", top: 5, left: 5, background: AA, color: "#000", fontSize: 6, fontWeight: 700, padding: "2px 5px", borderRadius: 3 } }, "FULL SET")),
    type === "core" && part.tag && React.createElement("div", { style: { fontSize: 6, color: active ? AA : "#6a7a60", letterSpacing: 2, padding: "3px 8px 0" } }, part.tag),
    React.createElement("div", { style: { fontSize: 10, color: "#bcc8d4", padding: "4px 8px 2px", lineHeight: 1.3 } }, part.name),
    React.createElement("div", { style: { fontFamily: "'Orbitron',sans-serif", fontSize: 10, fontWeight: 700, color: AA, padding: "0 8px 8px" } }, part.price > 0 ? (type === "core" ? "$" : "+$") + part.price : "Included"))); }
function OrderModal(props) { var sel = props.sel, price = props.price, onClose = props.onClose, cartErr = props.cartErr; return (React.createElement("div", { style: { position: "fixed", inset: 0, background: "rgba(4,6,14,0.96)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, backdropFilter: "blur(6px)" } },
    React.createElement("div", { style: { background: "#0a0b14", border: "1px solid rgba(224,112,32,0.2)", borderRadius: 10, padding: "22px 20px", width: "90%", maxWidth: 420 } },
        React.createElement("div", { style: { fontFamily: "'Orbitron',sans-serif", fontSize: 12, fontWeight: 700, color: "#e8f0d8", letterSpacing: 3, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 } },
            React.createElement("span", { style: { color: AA } }, "\u25C6"),
            " YOUR BUILD"),
        React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 0, marginBottom: 4 } },
            React.createElement("div", { style: { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #0e1520", fontSize: 9 } },
                React.createElement("span", { style: { color: "#364038", textTransform: "uppercase", width: 58, flexShrink: 0 } }, matchedFullBuild(sel) ? "Saber" : "Base"),
                React.createElement("span", { style: { flex: 1, color: "#8a9890" } }, matchedFullBuild(sel) ? matchedFullBuild(sel).name + " \u2014 Complete Saber" : "Xenopixel Core"),
                React.createElement("span", { style: { fontFamily: "'Orbitron',sans-serif", fontWeight: 700, color: AA, fontSize: 10 } },
                    "$",
                    matchedFullBuild(sel) ? FULL_BUILD_PRICE : BASE)),
            STEPS.map(function (k) { if (k === "core" || (matchedFullBuild(sel) && k !== "blade"))
                return null; var p = CAT[k].find(function (x) { return x.id === sel[k]; }); if (!p || p.price === 0)
                return null; return (React.createElement("div", { key: k, style: { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #0e1520", fontSize: 9 } },
                React.createElement("span", { style: { color: "#364038", textTransform: "uppercase", width: 58, flexShrink: 0 } }, LBL[k]),
                React.createElement("span", { style: { flex: 1, color: "#8a9890" } }, p.name),
                React.createElement("span", { style: { fontFamily: "'Orbitron',sans-serif", fontWeight: 700, color: AA, fontSize: 10 } }, matchedFullBuild(sel) && k === "blade" ? "Included" : "+$" + p.price))); })),
        React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "12px 0 14px", padding: "10px 0", borderTop: "1px solid rgba(224,112,32,0.2)", fontFamily: "'Orbitron',sans-serif", fontSize: 11, color: "#e8f0d8", letterSpacing: 2 } },
            React.createElement("span", null, "TOTAL"),
            React.createElement("span", { style: { fontSize: 20, fontWeight: 700, color: AA } },
                "$",
                price)),
        cartErr && React.createElement("div", { style: { margin: "0 0 10px", padding: "9px 11px", background: "rgba(180,40,30,0.14)", border: "1px solid rgba(220,80,60,0.45)", borderRadius: 6, color: "#ff9a86", fontSize: 10, lineHeight: 1.5 } }, cartErr),
        React.createElement("div", { style: { display: "flex", gap: 7 } },
            React.createElement("button", { onClick: onClose, style: { flex: 1, padding: "10px", background: "transparent", border: "1px solid #1a2028", color: "#364030", borderRadius: 5, cursor: "pointer", fontFamily: "inherit", fontSize: 8 } }, "\u2190 EDIT"),
            React.createElement("button", { onClick: props.onCheckout, style: { flex: 2, padding: "10px", background: "linear-gradient(135deg,#7a2800,#c04000)", border: "1px solid " + AA, color: "#ffe8c8", borderRadius: 5, cursor: "pointer", fontFamily: "inherit", fontSize: 8, fontWeight: 700 } }, "CHECKOUT \u2192"))))); }


/* ── Auto-mount: renders into <div id="saber-builder-root"> ── */
(function(){
  function __mount(){
    var el=document.getElementById("saber-builder-root");
    if(!el){console.warn("[saber-builder] #saber-builder-root not found");return;}
    __cr(el).render(React.createElement(SaberBuilder));
  }
  if(document.readyState==="loading"){document.addEventListener("DOMContentLoaded",__mount);}else{__mount();}
})();
